import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { COMPANY_INFO, MENU_ITEMS } from '../data/company';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-200 bg-white border-b ${scrolled ? 'border-slate-200 shadow-sm pt-0 pb-0' : 'border-slate-100 pt-0 pb-0'}`}>
      {/* Top microbar */}
      <div className="bg-slate-900 text-slate-300 text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <i className="fa-solid fa-location-dot text-sky-400" aria-hidden="true" />
              <span>{COMPANY_INFO.address}</span>
            </span>
            <span className="hidden sm:inline-block text-slate-600">|</span>
            <span className="hidden sm:inline-flex items-center gap-1.5">
              <i className="fa-solid fa-users text-sky-400" aria-hidden="true" />
              <span>{COMPANY_INFO.employeesCount}</span>
            </span>
          </div>

          <div className="flex items-center gap-4 ml-auto">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="inline-flex items-center gap-1.5 text-white hover:text-sky-400 font-semibold transition-colors whitespace-nowrap-custom"
            >
              <i className="fa-solid fa-phone text-sky-400 text-xs" aria-hidden="true" />
              <span>{COMPANY_INFO.phone}</span>
            </a>
            <span className="text-slate-600">|</span>
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="hidden md:inline-flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors whitespace-nowrap-custom"
            >
              <i className="fa-solid fa-envelope text-sky-400 text-xs" aria-hidden="true" />
              <span>{COMPANY_INFO.email}</span>
            </a>
            <a
              href={COMPANY_INFO.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-sky-400 transition-colors p-1"
              aria-label="OP Rengøringsservice på Facebook"
            >
              <i className="fa-brands fa-facebook text-sm" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-lg p-1">
            <img
              src={COMPANY_INFO.logoUrl}
              alt="OP Rengøringsservice Logo"
              className="h-10 sm:h-12 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (Visible strictly at >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center space-x-1" aria-label="Hovednavigation">
            {MENU_ITEMS.map((item) => {
              if (item.children && item.children.length > 0) {
                const isChildActive = item.children.some(c => location.pathname === c.href);
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isChildActive || location.pathname === item.href
                          ? 'text-blue-700 bg-blue-50'
                          : 'text-slate-700 hover:text-blue-700 hover:bg-slate-50'
                      }`}
                      aria-expanded={dropdownOpen}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>

                    {/* Dropdown menu */}
                    {dropdownOpen && (
                      <div className="absolute left-0 top-full pt-1 w-64 z-50">
                        <div className="rounded-2xl bg-white shadow-xl border border-slate-100 py-2.5 animate-in fade-in slide-in-from-top-2 duration-150 max-h-[calc(100vh-6rem)] overflow-y-auto">
                          <div className="px-4 py-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                            Vores Ydelser
                          </div>
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className={`flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors ${
                                location.pathname === child.href
                                  ? 'text-blue-700 bg-blue-50 font-semibold'
                                  : 'text-slate-700 hover:text-blue-700 hover:bg-slate-50'
                              }`}
                            >
                              <span>{child.label}</span>
                              <i className="fa-solid fa-arrow-right text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === "/"}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-semibold rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isActive
                        ? 'text-blue-700 bg-blue-50 font-bold'
                        : 'text-slate-700 hover:text-blue-700 hover:bg-slate-50'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Header Action CTAs (Desktop >= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center gap-3">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="inline-flex items-center gap-2 text-slate-700 hover:text-blue-700 font-bold text-sm px-4 py-2 rounded-full hover:bg-slate-50 transition-colors whitespace-nowrap-custom"
            >
              <i className="fa-solid fa-phone text-blue-600" aria-hidden="true" />
              <span>{COMPANY_INFO.phone}</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs uppercase tracking-wider px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile Burger Button (Visible strictly below 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-2">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="p-2 text-blue-700 hover:text-blue-800 bg-blue-50 rounded-lg text-sm font-bold flex items-center gap-1.5 whitespace-nowrap-custom"
              aria-label="Ring til os"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="hidden sm:inline text-xs">{COMPANY_INFO.phone}</span>
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-lg text-slate-700 hover:text-sky-800 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Slide-down Navigation (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-100 bg-white px-4 pt-3 pb-6 shadow-xl animate-in slide-in-from-top-2 duration-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="flex flex-col space-y-1">
            {MENU_ITEMS.map((item) => {
              if (item.children && item.children.length > 0) {
                return (
                  <div key={item.label} className="py-1">
                    <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                      {item.label}
                    </div>
                    <div className="pl-2 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            location.pathname === child.href
                              ? 'text-sky-800 bg-sky-50 font-bold'
                              : 'text-slate-700 hover:text-sky-800 hover:bg-slate-50'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === "/"}
                  className={({ isActive }) =>
                    `block px-3 py-2.5 rounded-lg text-base font-semibold transition-colors ${
                      isActive
                        ? 'text-sky-800 bg-sky-50 font-bold'
                        : 'text-slate-800 hover:text-sky-800 hover:bg-slate-50'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="mt-6 pt-4 border-t border-slate-100 space-y-3">
            <Link
              to="/kontakt"
              className="flex items-center justify-center gap-2 w-full bg-sky-800 text-white font-semibold text-center py-3 rounded-lg shadow-sm"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>

            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="flex items-center justify-center gap-2 w-full bg-slate-100 text-slate-800 font-bold text-center py-2.5 rounded-lg whitespace-nowrap-custom"
            >
              <i className="fa-solid fa-phone text-blue-600" aria-hidden="true" />
              <span>{COMPANY_INFO.phone}</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
